// ========= Enums =========
export enum BillingTransactionType {
  PAYMENT = "PAYMENT",
  REFUND = "REFUND",
  PARTIAL_REFUND = "PARTIAL_REFUND",
}

export enum BillingTransactionFilterType {
  PAYMENT = "PAYMENT",
  REFUND = "REFUND",
  PARTIAL_REFUND = "PARTIAL_REFUND",
}

export enum BillingTransactionStatus {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  SUCCEEDED = "SUCCEEDED",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
  REFUNDED = "REFUNDED",
  PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
  REQUIRES_ACTION = "REQUIRES_ACTION",
  REQUIRES_PAYMENT_METHOD = "REQUIRES_PAYMENT_METHOD",
}

export enum BillingPaymentMethod {
  ONLINE = "ONLINE",
  OFFLINE = "OFFLINE",
}

export interface BillingTransaction {
  id: string;
  hotelId: string;
  tenantId: string;
  guestSummary?: BillingTransactionGuestSummary | null;

  stripePaymentIntentId?: string | null;
  stripeChargeId?: string | null;
  stripeTransferId?: string | null;
  stripeRefundId?: string | null;

  type: BillingTransactionType;
  status: BillingTransactionStatus;
  paymentMethod: BillingPaymentMethod;

  amount: number;
  currency: string;

  applicationFeeAmount?: number | null;
  netAmount?: number | null;

  bookingId?: string | null;
  booking?: ITransactionBooking | null;

  billingItems?: ITransactionBillingItem[] | null;

  metadata: Record<string, unknown>;

  failureCode?: string | null;
  failureMessage?: string | null;

  createdAt: Date | string;
  completedAt?: Date | string | null;
  refundedAt?: Date | string | null;
}

export interface ITransactionBillingItem {
  id: string;
  type: BillingItemType;
  guestId: string;
  serviceRequestId?: string;
  serviceId?: string;
  amount: number;
  description?: string;
  paymentStatus: BillingPaymentStatus;

  service?: Record<string, unknown>;
  serviceRequest?: Record<string, unknown>;
  guest?: BillingTransactionGuestSummary | null;
}

export enum BillingItemType {
  SERVICE = "SERVICE",
  ROOM = "ROOM",
}

export enum BillingPaymentStatus {
  PENDING = "PENDING",
  SUCCEEDED = "SUCCEEDED",
  NOT_REQUIRED = "NOT_REQUIRED",
  FAILED = "FAILED",
  REFUNDED = "REFUNDED",
  PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
}

export interface ITransactionBooking {
  id: string;
  guestId: string;
  guest?: BillingTransactionGuestSummary | null;
  hotelId: string;
  roomId: string;
  status: string;
  arrivalDate: string; // ISO 8601
  departureDate: string; // ISO 8601
  bookingNumber: number;
}

export interface BillingTransactionGuestSummary {
  id: string;
  displayName?: string | null;
  initials?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface BillingTransactionServiceRequest {
  id: string;
  guestId: string;
  bookingId: string;
  status: string;
  priority: boolean;
  paymentStatus: string;
  createdAt: string; // ISO 8601
}

export interface BillingTransactionService {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  paymentEnabled: boolean;
  paymentRequired: boolean;
}

// ========= Summary DTO =========
export interface BillingTransactionsSummary {
  /** Total number of transactions (for the current filter) */
  totalCount: number;

  /** Total amount (usually sum of amounts, in smallest currency unit) */
  totalAmount: number;

  succeededCount: number;
  failedCount: number;
  pendingCount: number;
}

export interface BillingTransactionsListResponse {
  /** List of transactions */
  transactions: BillingTransaction[];

  /** Summary statistics */
  summary: BillingTransactionsSummary;

  /** Total number of transactions matching the filter */
  total: number;

  /** Current page number */
  page: number;

  /** Number of items per page */
  limit: number;

  /** Total number of pages */
  totalPages: number;
}

export interface BillingTransactionsFilters {
  /** Filter by hotel ID */
  hotelId?: string;

  /** Filter by service ID */
  serviceId?: string;

  /** Filter by guest ID */
  guestId?: string;

  /** Filter by booking ID */
  bookingId?: string;

  /** Filter by service request ID */
  serviceRequestId?: string;

  /** Filter by transaction status */
  status?: BillingTransactionStatus | "all statuses";

  /** Filter by transaction type */
  type?: BillingTransactionFilterType | "all types";

  /** Filter by payment method */
  paymentMethod?: BillingPaymentMethod | "all methods";

  /** Filter from date (ISO 8601) */
  fromDate?: Date | string;

  /** Filter to date (ISO 8601) */
  toDate?: Date | string;

  /** Minimum amount (in smallest currency unit, e.g. cents) */
  minAmount?: number;

  /** Maximum amount (in smallest currency unit, e.g. cents) */
  maxAmount?: number;

  /** Page number (default: 1) */
  page?: number;

  /** Items per page (default: 20) */
  limit?: number;
}

export type BillingTransactionStatsFilters = Omit<
  BillingTransactionsFilters,
  "page" | "limit"
>;

export interface BillingTransactionStats {
  currency: string | null;
  totalRevenue: number;
  totalCount: number;
  succeededCount: number;
  failedCount: number;
  pendingCount: number;
  averageCheck: number;
}
